import Link from "next/link";
import { useRouter } from "next/navigation";
import React, { useCallback } from "react";
import { NavLabelKey } from "../constants";

interface MobileTopBarProps {
  t: (key: string) => string;
  phase: 0 | 1 | 2;
  hasUserId: boolean;
  mobileNavOpen: boolean;
  isLangOpen: boolean;
  isPastTransactionsPage: boolean;
  pastTransactionsHref: string;
  userDisplayName: string;
  isPremiumUser: boolean;
  mobileExpandedHeight: string;
  setMobileNavOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLangOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setLoginOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleLogout: () => void;
  handleLogoClick: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  scrollToSection: (
    sectionId: string,
    labelKey: NavLabelKey,
    shouldCloseMobileNav?: boolean,
  ) => void;
  navItems: readonly {
    labelKey: NavLabelKey;
    sectionId: string;
  }[];
  mobileNavButtonClass: (labelKey: NavLabelKey) => string;
  onDeleteAccount: () => void;
}

const MobileTopBar = (props: MobileTopBarProps) => {
  const {
    t,
    phase,
    hasUserId,
    mobileNavOpen,
    isLangOpen,
    isPastTransactionsPage,
    pastTransactionsHref,
    userDisplayName,
    isPremiumUser,
    mobileExpandedHeight,
    setMobileNavOpen,
    setIsLangOpen,
    setLoginOpen,
    handleLogout,
    handleLogoClick,
    scrollToSection,
    navItems,
    mobileNavButtonClass,
    onDeleteAccount,
  } = props;

  const router = useRouter();

  const handlePastTransactionsClick = useCallback(() => {
    if (mobileNavOpen || isLangOpen) {
      setMobileNavOpen(false);
      setIsLangOpen(false);
      const delay = mobileNavOpen ? 420 : 200;
      window.setTimeout(() => router.push(pastTransactionsHref), delay);
    } else {
      router.push(pastTransactionsHref);
    }
  }, [mobileNavOpen, isLangOpen, setMobileNavOpen, setIsLangOpen, router, pastTransactionsHref]);

  return (
    <div
      data-mobile-nav
      className="lg:hidden topbar-compact font-bold pointer-events-auto bg-white shadow-lg rounded-3xl w-full overflow-hidden"
      style={{
        height: mobileExpandedHeight,
        maxHeight: "calc(100vh - 2rem)",
        overflowY: mobileNavOpen ? "auto" : "hidden",
        transform: phase >= 1 ? "translateY(0)" : "translateY(-100px)",
        transition:
          "transform 0.7s cubic-bezier(0.65,0,0.35,1), height 0.45s cubic-bezier(0.65,0,0.35,1)",
      }}
    >
      <div className="shrink-0 px-3 pt-2">
        <div
          className={`flex items-center justify-between transition-all duration-200 ${isLangOpen ? "h-8" : "h-11"}`}
        >
          <Link href="/" onClick={handleLogoClick}>
            <img
              src="/logo/agar_mart_logo.svg"
              alt="AgarMart Logo"
              className="h-8 w-auto"
            />
          </Link>

          <div className="flex items-center gap-2">
            <button
              data-lang-dropdown
              type="button"
              onClick={() => { setIsLangOpen((value) => !value); setMobileNavOpen(false); }}
              className="flex items-center justify-center h-9 w-9 rounded-full bg-[#0A2540] text-white shrink-0"
              aria-label={t("changeLanguage")}
            >
              <img
                src="/logo/globe_logo.svg"
                alt={t("changeLanguage")}
                className="h-5 w-5"
              />
            </button>

            {hasUserId && (
              <div className="relative group">
                <button
                  type="button"
                  onClick={handlePastTransactionsClick}
                  className={`flex items-center justify-center h-9 w-9 rounded-full shrink-0 ${isPastTransactionsPage ? "text-[#117DD4]" : "text-[#0A2540]"
                    }`}
                  style={{ backgroundColor: "rgba(17, 125, 212, 0.08)" }}
                  aria-label={t("pastTransactions")}
                >
                  <svg
                    className="h-5 w-5"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8v4l3 3M3.22302 14C4.13247 18.008 7.71683 21 12 21c4.9706 0 9-4.0294 9-9 0-4.97056-4.0294-9-9-9-3.72916 0-6.92858 2.26806-8.29409 5.5M7 9H3V5"
                    />
                  </svg>
                </button>
                <span className="absolute left-1/2 -translate-x-1/2 top-full mt-2 whitespace-nowrap rounded bg-gray-800 text-white text-xs px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                  {t("pastTransactions")}
                </span>
              </div>
            )}

            {hasUserId && (
              <button
                type="button"
                className="flex items-center justify-center h-9 w-9 rounded-full bg-red-50 text-red-500 transition-colors shrink-0"
                onClick={handleLogout}
                aria-label={t("profileLogout")}
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
              </button>
            )}

            {!hasUserId && (
              <button
                type="button"
                className="flex items-center justify-center h-9 w-9 rounded-full bg-[#117DD4] text-white shrink-0"
                onClick={() => setLoginOpen(true)}
                aria-label={t("profileLogin")}
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4M10 17l5-5m0 0l-5-5m5 5H3"
                  />
                </svg>
              </button>
            )}

            <button
              type="button"
              onClick={() => { setMobileNavOpen((value) => !value); setIsLangOpen(false); }}
              className="relative w-9 h-9 flex items-center justify-center shrink-0 focus:outline-none rounded-full bg-[#0A2540] text-white transition-colors duration-200 hover:bg-slate-900"
              aria-label={mobileNavOpen ? "Close menu" : "Open menu"}
            >
              <span
                className="absolute block w-5 bg-white rounded-full transition-all duration-300"
                style={{
                  height: "2px",
                  top: mobileNavOpen ? "50%" : "calc(50% - 5px)",
                  transform: mobileNavOpen
                    ? "translateY(-50%) rotate(45deg)"
                    : "none",
                }}
              />
              <span
                className="absolute block w-5 bg-white rounded-full transition-all duration-300"
                style={{
                  height: "2px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  opacity: mobileNavOpen ? 0 : 1,
                }}
              />
              <span
                className="absolute block w-5 bg-white rounded-full transition-all duration-300"
                style={{
                  height: "2px",
                  top: mobileNavOpen ? "50%" : "calc(50% + 5px)",
                  transform: mobileNavOpen
                    ? "translateY(-50%) rotate(-45deg)"
                    : "none",
                }}
              />
            </button>
          </div>
        </div>

        {hasUserId && mobileNavOpen && (
          <div className="md:hidden mt-2 flex items-center justify-between gap-2 rounded-3xl border border-amber-200 bg-gradient-to-r from-amber-50 via-white to-yellow-50 px-2 py-1.5 shadow-sm">
            <div className="flex min-w-0 items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0A2540] text-white shadow-sm shrink-0">
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.75 6.75a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0ZM4.5 20.118a7.5 7.5 0 0115 0A17.933 17.933 0 0112 21.75a17.933 17.933 0 01-7.5-1.632Z"
                  />
                </svg>
              </span>
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-[#0A2540]">
                  {userDisplayName}
                </div>
                <div className="truncate text-[11px] font-medium text-slate-500">
                  {t("welcome")}
                </div>
              </div>
            </div>

            {isPremiumUser && (
              <span
                className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-linear-to-r from-amber-400 to-yellow-300 px-2 py-1 text-[10px] font-extrabold uppercase tracking-[0.16em] text-[#5b3a00] shadow-sm shrink-0"
                aria-label={t("premiumBadgeAria")}
                title={t("premiumBadge")}
              >
                <svg
                  className="h-3 w-3"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M10 1.75l2.15 4.36 4.81.7-3.48 3.39.82 4.79L10 12.73 5.7 14.99l.82-4.79-3.48-3.39 4.81-.7L10 1.75Z" />
                </svg>
                {t("premiumBadge")}
              </span>
            )}
          </div>
        )}
      </div>
      <div
        className={`flex justify-center px-3 pt-2 pb-4 transition-all duration-200 md:absolute md:right-0 md:w-64 md:rounded-2xl md:bg-white md:border md:border-slate-200 md:shadow-lg md:z-99999 md:p-3 ${mobileNavOpen
          ? "md:opacity-100 md:pointer-events-auto md:scale-100"
          : "md:opacity-0 md:pointer-events-none md:scale-95"
          }`}
        style={{ transformOrigin: "top right", top: "calc(100% + 8px)" }}
      >
        <div className="flex flex-col gap-2 w-full mx-auto">
          {hasUserId && (
            <div className="hidden md:block px-3 py-2 mb-2 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 via-white to-yellow-50 shadow-sm">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0A2540] text-white shadow-sm shrink-0">
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15.75 6.75a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0ZM4.5 20.118a7.5 7.5 0 0115 0A17.933 17.933 0 0112 21.75a17.933 17.933 0 01-7.5-1.632Z"
                    />
                  </svg>
                </span>
                <div className="min-w-0 text-left flex-1">
                  <div className="truncate text-sm font-semibold text-[#0A2540]">
                    {userDisplayName}
                  </div>
                  <div className="truncate text-[11px] font-medium text-slate-500">
                    {t("welcome")}
                  </div>
                </div>
                {isPremiumUser && (
                  <span
                    className="inline-flex items-center gap-0.5 rounded-full border border-amber-300 bg-linear-to-r from-amber-400 to-yellow-300 px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-[0.1em] text-[#5b3a00] shadow-sm shrink-0"
                    aria-label={t("premiumBadgeAria")}
                    title={t("premiumBadge")}
                  >
                    <svg
                      className="h-2.5 w-2.5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M10 1.75l2.15 4.36 4.81.7-3.48 3.39.82 4.79L10 12.73 5.7 14.99l.82-4.79-3.48-3.39 4.81-.7L10 1.75Z" />
                    </svg>
                    {t("premiumBadge")}
                  </span>
                )}
              </div>
            </div>
          )}
          {navItems.map(({ labelKey, sectionId }, index) => (
            <button
              key={labelKey}
              type="button"
              onClick={() => scrollToSection(sectionId, labelKey, true)}
              className={mobileNavButtonClass(labelKey)}
              style={{
                transform: mobileNavOpen ? "translateY(0)" : "translateY(-8px)",
                opacity: mobileNavOpen ? 1 : 0,
                transition: `transform 0.35s cubic-bezier(0.65,0,0.35,1) ${mobileNavOpen ? index * 50 : 0}ms, opacity 0.3s ease ${mobileNavOpen ? index * 50 : 0}ms`,
              }}
            >
              {t(labelKey)}
            </button>
          ))}
          {hasUserId && (
            <button
              type="button"
              onClick={() => {
                onDeleteAccount();
              }}
              className="px-3 py-2 rounded-2xl text-sm font-medium transition-colors duration-150 cursor-pointer text-left text-red-500 hover:bg-red-50"
              style={{
                transform: mobileNavOpen ? "translateY(0)" : "translateY(-8px)",
                opacity: mobileNavOpen ? 1 : 0,
                transition: `transform 0.35s cubic-bezier(0.65,0,0.35,1) ${mobileNavOpen ? navItems.length * 50 : 0}ms, opacity 0.3s ease ${mobileNavOpen ? navItems.length * 50 : 0}ms`,
              }}
            >
              {t("deleteAccount")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileTopBar;
